import { Injectable } from 'framework/di';

import type { ProductItemVariant } from '../../providers/boost/models';
import { AppService } from '../app/app.service';
import { PlatformLoader } from '../platform/platform.loader';
import { TemplateAPI } from '../template/template.api';
import { VolumeBundleAPI } from './volume-bundle.api';
import { VolumeBundleConstants } from './volume-bundle.constants';
import type {
  DiscountedPriceResult,
  FormatPriceParams,
  VolumeBundleAction,
  VolumeBundleCartItem,
  VolumeBundleData,
  VolumeBundleDiscount,
  VolumeBundleOption,
  VolumeBundleProduct,
  VolumeBundleTemplateName,
  VolumeBundleWidgetDesignSettings,
} from './volume-bundle.model';

@Injectable
export class VolumeBundleService {
  private bundleDataMap = new Map<string, VolumeBundleData>();

  constructor(
    protected appService: AppService,
    protected platformLoader: PlatformLoader,
    protected templateAPI: TemplateAPI,
    protected volumeBundleAPI: VolumeBundleAPI,
    protected constants: VolumeBundleConstants
  ) {}

  get widgets() {
    return this.appService.TAEAppConfig.recommendationWidgets?.['dynamic-bundle'] || {};
  }

  get defaultSettings() {
    return (
      this.appService.TAEAppConfig.recommendationWidgets?.dynamicBundleDefaultSettings ||
      this.constants.DEFAULT_SETTINGS
    );
  }

  isValidBundle(widgetId?: string): boolean {
    if (!widgetId || widgetId === '') return false;

    const recommendationWidgets = this.appService.TAEAppConfig.recommendationWidgets;
    if (!recommendationWidgets || Object.keys(recommendationWidgets).length === 0) return false;

    const volumeBundleWidgets = recommendationWidgets['dynamic-bundle'];
    if (!volumeBundleWidgets || Object.keys(volumeBundleWidgets).length === 0) return false;

    return true;
  }

  async getWidgetTemplate(
    templateName: VolumeBundleTemplateName = this.constants.BUNDLE_TEMPLATE_NAME.bundleBlock
  ): Promise<string> {
    const { templateMetadata } = this.appService.TAEAppConfig;
    const templateId = templateMetadata?.[templateName];

    const { templates } = await this.templateAPI.getWidgetTemplate({
      name: templateName,
      id: templateId,
    });

    return templates[templateId] || '';
  }

  getWidgetSettings(widgetId: string): {
    widgetDesignSettings: VolumeBundleWidgetDesignSettings;
  } {
    let widgetDesignSettings =
      this.appService.TAEAppConfig.recommendationWidgets?.['dynamic-bundle']?.[widgetId]
        ?.widgetDesignSettings || {};

    widgetDesignSettings = {
      ...this.defaultSettings,
      ...widgetDesignSettings,
    };

    const { titleFontStyle } = widgetDesignSettings;
    if (titleFontStyle) {
      const { fontWeight, fontStyle } = this.appService.splitGoogleFontStyle(titleFontStyle);
      widgetDesignSettings.titleFontWeight = fontWeight;
      widgetDesignSettings.titleFontStyle = fontStyle;
    }

    return { widgetDesignSettings };
  }

  formatPrice(params: FormatPriceParams): string {
    const { value, showCurrencyCodes, showCentAsSuperscript, removeDecimalPoint } = params;

    const formatString = this.appService.TAEAppConfig.shop?.money_format || '${{amount}}';

    return this.appService.formatCurrency(value, {
      format: formatString,
      showCentAsSuperscript,
      removePriceDecimal: removeDecimalPoint,
      showCurrencyCodes,
    });
  }

  calculateDiscountedPrice(
    totalOriginal: number,
    discount?: VolumeBundleDiscount,
    currencyRate?: number
  ): DiscountedPriceResult {
    if (!discount || !discount.discount_type) {
      return { rawDiscountedPrice: totalOriginal, rawOriginalPrice: totalOriginal };
    }

    const { discount_type, discount_value } = discount;
    const rate = currencyRate || 1;
    const rawDiscountedPrice =
      discount_type === 'percentage'
        ? totalOriginal - (totalOriginal * discount_value) / 100
        : Math.max(0, totalOriginal - discount_value * rate);

    return { rawDiscountedPrice, rawOriginalPrice: totalOriginal };
  }

  async processVolumeBundleData(
    bundle: VolumeBundleData,
    options: {
      optionData?: { variantId?: string | number };
      action?: VolumeBundleAction;
      target?: Element | NodeList | null;
      position?: string;
    }
  ): Promise<VolumeBundleData | undefined> {
    const widgetId = bundle.widgetId || '';
    const widgetDesignSettings =
      (this.appService.TAEAppConfig.recommendationWidgets?.['dynamic-bundle']?.[widgetId]
        ?.widgetDesignSettings as VolumeBundleWidgetDesignSettings) || {};
    const variantSelectionType = widgetDesignSettings.variantSelection || 'single';
    bundle.variantSelectionType = variantSelectionType;

    let product = bundle.products[0];

    if (
      this.platformLoader.platform.router.page === 'product' &&
      options.position === 'volumeBlock'
    ) {
      const productId = window.boostSDData?.product?.id;
      if (productId) {
        const fetchedProduct = await this.volumeBundleAPI.getProductById(productId);
        if (fetchedProduct) {
          const matchedProduct = bundle.products.find((p) => p.id === fetchedProduct.id);
          product = (matchedProduct || fetchedProduct) as VolumeBundleProduct;
        }
      }
    }

    if (!product) return;

    product.variants = product.variants?.filter(this.filterProductAvailable) || [];

    if (!Array.isArray(product.variants) || product.variants.length === 0) {
      console.warn('No valid variants found for product:', product.id);
      return;
    }

    let selectedVariant = product.variants.reduce((minVariant, currentVariant) => {
      if (!this.isValidVariant(currentVariant) || !this.isValidVariant(minVariant)) {
        return this.isValidVariant(minVariant) ? minVariant : currentVariant;
      }
      return parseFloat(currentVariant.price) < parseFloat(minVariant.price)
        ? currentVariant
        : minVariant;
    });

    if (options.action === this.constants.BUNDLE_ACTION.changeVariant) {
      const variantId = options.optionData?.variantId;
      const foundVariant = product.variants.find(
        (variant) => this.isValidVariant(variant) && Number(variant.id) === Number(variantId)
      );
      if (foundVariant) {
        selectedVariant = foundVariant;
      }
    }

    const { price: originPrice = 0, title = '', id = '', image = '' } = selectedVariant;

    product.selectedOption = {
      rawPrice: Number(originPrice),
      title,
      price: this.formatPrice({ value: Number(originPrice) }),
      originPrice: this.formatPrice({ value: Number(originPrice) }),
      minPrice: this.formatPrice({ value: Number(product.price_min) }),
      id,
      image: image ?? product.images_info?.[0]?.src,
    };

    const discounts = Array.isArray(bundle.discounts) ? bundle.discounts : [];
    const currencyRate = Number(window.Shopify?.currency?.rate || 1);
    bundle.bundleOptions = discounts.length
      ? discounts.map((discount, index) => {
          const itemsCount = discount.item;
          let totalOriginal = 0;

          if (variantSelectionType === 'multiple') {
            for (let i = 0; i < itemsCount; i++) {
              if (!Array.isArray(product.variants) || product.variants.length === 0) {
                console.warn('No variants available for multiple selection');
                break;
              }

              const variantIndex = i >= product.variants.length ? 0 : i;
              const variantCandidate = product.variants[variantIndex];

              if (!this.isValidVariant(variantCandidate)) {
                console.warn('Invalid variant at index', variantIndex);
                continue;
              }

              totalOriginal += Number(variantCandidate.price);
            }
          } else {
            totalOriginal = Number(originPrice) * itemsCount;
          }

          const { rawDiscountedPrice } = this.calculateDiscountedPrice(
            totalOriginal,
            discount,
            currencyRate
          );

          const discountValue = Number(discount.discount_value) * currencyRate;
          const showDiscount =
            discount.discount_type === 'percentage'
              ? discount.discount_value > 0
              : discountValue > 0;
          const hasDiscount = showDiscount && rawDiscountedPrice < totalOriginal;

          return {
            id: `bundle-option-${index + 1}`,
            checked: widgetDesignSettings.bundleHighlightOption === index + 1,
            items: itemsCount,
            hasDiscount,
            discountLabel: hasDiscount
              ? `Buy ${discount.item} items and save ${
                  discount.discount_type === 'percentage'
                    ? `${discount.discount_value}%`
                    : this.formatPrice({ value: discount.discount_value * currencyRate })
                }`
              : '',
            rawOriginalPrice: totalOriginal,
            rawDiscountedPrice,
            originalPrice: this.formatPrice({ value: totalOriginal }),
            discountedPrice: this.formatPrice({ value: rawDiscountedPrice }),
            discountValueDisplay:
              hasDiscount && discount.discount_type === 'percentage'
                ? `${discount.discount_value}%`
                : hasDiscount
                  ? this.formatPrice({ value: discount.discount_value * currencyRate })
                  : '',
            selectedDropdownOptions: {
              variantId: selectedVariant.id,
              discountedPrice: this.formatPrice({ value: rawDiscountedPrice }),
              originalPrice: this.formatPrice({ value: totalOriginal }),
              rawDiscountedPrice,
              title: selectedVariant.title,
              image: selectedVariant.image || product.images_info?.[0]?.src,
            },
          };
        })
      : [
          {
            id: 'bundle-option-0',
            checked: widgetDesignSettings.bundleHighlightOption === 0,
            items: 1,
            discountLabel: 'No Discount',
            rawOriginalPrice: Number(originPrice),
            rawDiscountedPrice: Number(originPrice),
            originalPrice: this.formatPrice({ value: Number(originPrice) }),
            discountedPrice: this.formatPrice({ value: Number(originPrice) }),
            discountValueDisplay: '0%',
            selectedDropdownOptions: {
              variantId: selectedVariant.id,
              discountedPrice: this.formatPrice({ value: Number(originPrice) }),
              originalPrice: this.formatPrice({ value: Number(originPrice) }),
              rawDiscountedPrice: Number(originPrice),
              title: selectedVariant.title,
              image: selectedVariant.image || product.images_info?.[0]?.src,
            },
          },
        ];

    let target = options.target;
    if (target instanceof NodeList) {
      target = target[0] as Element;
    }

    const selectedItems =
      Number(
        (target as Element)
          ?.closest(this.constants.SELECTORS.VOLUME_MENU_ITEM)
          ?.getAttribute('data-items')
      ) || bundle.bundleOptions[0].items;
    bundle.selectedVariant = bundle.bundleOptions.find((option) => option.items === selectedItems);
    bundle.matchedProduct = product as VolumeBundleProduct;
    bundle.productMinPrice = this.formatPrice({ value: Number(product.price_min) });

    const selectedBundleOption = bundle.bundleOptions.find((option) => option.checked);
    if (selectedBundleOption) {
      bundle.selectedOption = {
        ...selectedBundleOption,
        discountedPrice: this.formatPrice({ value: selectedBundleOption.rawDiscountedPrice }),
        originalPrice: this.formatPrice({ value: selectedBundleOption.rawOriginalPrice }),
      };
    }

    this.setBundleData(bundle.widgetId, bundle);

    const totalBundlePrice = bundle.selectedOption?.discountedPrice || 0;
    const newBundleDataSession = {
      ...this.getBundleSessionData(this.constants.SESSION_KEYS.DATA_SESSION),
      [bundle.widgetId]: {
        widgetId: bundle.widgetId,
        bundle_type: bundle.bundle_type,
        recommendationType: bundle.recommendation_type,
        primary_algorithm: bundle.primary_algorithm || '',
        secondary_algorithm: bundle.secondary_algorithm || '',
        totalBundlePrice,
        bundleProductId: product.id,
        discountApplied: selectedBundleOption?.hasDiscount || false,
      },
    };
    this.saveBundleSessionData(this.constants.SESSION_KEYS.DATA_SESSION, newBundleDataSession);

    return bundle;
  }

  async updateVolumeBundleData(
    widgetId: string,
    data: { variantId?: string | number },
    action: VolumeBundleAction,
    target: Element | null,
    position?: string
  ): Promise<VolumeBundleData | undefined> {
    const oldDataVolumeBundle = this.getBundleData(widgetId);

    if (!oldDataVolumeBundle) {
      console.error('Bundle data not found');
      return;
    }

    const newBundleData = await this.processVolumeBundleData(oldDataVolumeBundle, {
      optionData: data,
      action,
      target,
      position,
    });

    const checkedBundleOption = newBundleData?.bundleOptions?.find((option) => option.checked);

    const mergedBundleData = {
      ...oldDataVolumeBundle,
      matchedProduct: newBundleData?.matchedProduct,
      selectedVariant: newBundleData?.selectedVariant,
      selectedOption: checkedBundleOption
        ? {
            ...checkedBundleOption,
            discountedPrice: this.formatPrice({ value: checkedBundleOption.rawDiscountedPrice }),
            originalPrice: this.formatPrice({ value: checkedBundleOption.rawOriginalPrice }),
          }
        : null,
    };

    this.setBundleData(widgetId, mergedBundleData as VolumeBundleData);
    return mergedBundleData as VolumeBundleData;
  }

  buildCartPayload(bundleData: VolumeBundleData, target?: Element | null): VolumeBundleCartItem[] {
    const data: VolumeBundleCartItem[] = [];
    const selectedOption = bundleData.selectedOption;

    if (!selectedOption || !bundleData.matchedProduct) {
      return data;
    }

    const widgetDesignSettings =
      (this.appService.TAEAppConfig.recommendationWidgets?.['dynamic-bundle']?.[bundleData.widgetId]
        ?.widgetDesignSettings as VolumeBundleWidgetDesignSettings) || {};
    const variantSelectionType = widgetDesignSettings.variantSelection || 'single';

    let bundleContainer: Element | null = null;
    if (bundleData.widgetId) {
      bundleContainer =
        document.querySelector(
          `${this.constants.SELECTORS.RECOMMENDATION}[data-widget-id="${bundleData.widgetId}"] ${this.constants.SELECTORS.VOLUME_MENU}`
        ) ||
        document.querySelector(
          `[data-widget-id="${bundleData.widgetId}"] ${this.constants.SELECTORS.VOLUME_MENU}`
        );
    }

    if (!bundleContainer) {
      bundleContainer = document.querySelector(this.constants.SELECTORS.VOLUME_MENU);
    }

    const bundleProductId = bundleData.matchedProduct?.id;

    if (variantSelectionType === 'multiple') {
      if (!bundleContainer) {
        console.warn(
          'Bundle container not found for multiple selection — fallback to single add behavior.'
        );
        data.push({
          id: String(selectedOption.selectedDropdownOptions.variantId),
          quantity: selectedOption.items,
          properties: {
            BoostBundle: '',
            _boostBundleId: bundleData.widgetId,
            _boostBundleType: bundleData.bundle_type,
            _boostItemPrice: selectedOption.selectedDropdownOptions.rawDiscountedPrice,
            _boostItemTitle: selectedOption.selectedDropdownOptions.title,
            _boostBundleProducts: bundleProductId,
          },
        });
      } else {
        const activeItem = this.getActiveItem(bundleContainer);

        const variantRows =
          activeItem?.querySelectorAll('.boost-sd__volume-bundle-variant-row') || [];

        if (!variantRows || variantRows.length === 0) {
          data.push({
            id: String(selectedOption.selectedDropdownOptions.variantId),
            quantity: selectedOption.items,
            properties: {
              BoostBundle: '',
              _boostBundleId: bundleData.widgetId,
              _boostBundleType: bundleData.bundle_type,
              _boostItemPrice: selectedOption.selectedDropdownOptions.rawDiscountedPrice,
              _boostItemTitle: selectedOption.selectedDropdownOptions.title,
              _boostBundleProducts: bundleProductId,
            },
          });
        } else {
          variantRows.forEach((row) =>
            this.handleVariantRow(row, data, selectedOption, bundleData, bundleProductId)
          );
        }
      }
    } else {
      data.push({
        id: String(selectedOption.selectedDropdownOptions.variantId),
        quantity: selectedOption.items,
        properties: {
          BoostBundle: '',
          _boostBundleId: bundleData.widgetId,
          _boostBundleType: bundleData.bundle_type,
          _boostItemPrice: selectedOption.selectedDropdownOptions.rawDiscountedPrice,
          _boostItemTitle: selectedOption.selectedDropdownOptions.title,
          _boostBundleProducts: bundleData.matchedProduct.id,
        },
      });
    }

    return data;
  }

  updateMultipleBundlePrice(bundleContainer: Element, specificItem?: Element | null): void {
    const widgetRoot =
      bundleContainer?.closest(this.constants.SELECTORS.RECOMMENDATION) ||
      bundleContainer?.closest('[data-widget-id]');
    const widgetId = widgetRoot?.getAttribute('data-widget-id') || null;

    if (!bundleContainer || !widgetId) return;

    const bundleState = this.getBundleData(widgetId);
    const product = bundleState?.matchedProduct;
    const discounts = Array.isArray(bundleState?.discounts) ? bundleState.discounts : [];
    if (!product?.variants?.length) return;

    const activeItem = this.getActiveItem(bundleContainer, specificItem);
    if (!activeItem) return;

    const { totalOriginal, discounted, selectedVariants } = this.calculateTotalAndDiscount(
      activeItem,
      product,
      discounts
    );

    const discountedEl = activeItem.querySelector('.boost-sd__volume-bundle-menu-discounted-price');
    const originalEl = activeItem.querySelector('.boost-sd__volume-bundle-menu-original-price');

    if (discountedEl) discountedEl.textContent = this.formatPrice({ value: discounted });
    if (originalEl) originalEl.textContent = this.formatPrice({ value: totalOriginal });

    bundleContainer.setAttribute('data-selected-variants', JSON.stringify(selectedVariants));
  }

  saveBundleSessionData(key: string, data: unknown): void {
    try {
      sessionStorage.setItem(key, JSON.stringify(data));
    } catch {}
  }

  getBundleSessionData(key: string): Record<string, unknown> {
    try {
      const data = sessionStorage.getItem(key);
      return data ? JSON.parse(data) : {};
    } catch {
      return {};
    }
  }

  getBundleData(widgetId: string): VolumeBundleData | undefined {
    return this.bundleDataMap.get(`${this.constants.KEY_VOLUME_BUNDLE}-${widgetId}`);
  }

  setBundleData(widgetId: string, data: VolumeBundleData): void {
    this.bundleDataMap.set(`${this.constants.KEY_VOLUME_BUNDLE}-${widgetId}`, data);
  }

  getLocale(): string {
    return (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
  }

  private filterProductAvailable(variant: ProductItemVariant): boolean {
    return variant?.available === true;
  }

  private isValidVariant(variant: unknown): variant is ProductItemVariant {
    return Boolean(
      variant &&
        typeof variant === 'object' &&
        !Array.isArray(variant) &&
        typeof (variant as ProductItemVariant).id !== 'undefined' &&
        typeof (variant as ProductItemVariant).price !== 'undefined' &&
        (variant as ProductItemVariant).price !== null &&
        !isNaN(Number((variant as ProductItemVariant).price))
    );
  }

  private getSelectedItem(row: Element): Element | null {
    return (
      row.querySelector('.boost-sd__dropdown-option--selected') ||
      row.querySelector('.boost-sd__dropdown-volume-item.boost-sd__dropdown-option--selected') ||
      row.querySelector('.boost-sd__dropdown-volume-item[data-variant-id]')
    );
  }

  private getActiveItem(bundleContainer: Element, specificItem?: Element | null): Element | null {
    return (
      specificItem ||
      bundleContainer
        .querySelector('.boost-sd__volume-bundle-menu-radio--checked')
        ?.closest('.boost-sd__volume-bundle-menu-item') ||
      bundleContainer.querySelector('.boost-sd__volume-bundle-menu-item--highlight')
    );
  }

  private calculateTotalAndDiscount(
    activeItem: Element,
    product: VolumeBundleProduct,
    discounts: VolumeBundleDiscount[]
  ): { totalOriginal: number; discounted: number; selectedVariants: string[] } {
    const itemsCount = Number(activeItem.getAttribute('data-items') || 0);
    const discount = discounts.find((d) => Number(d.item) === itemsCount);
    const variantRows = activeItem.querySelectorAll('.boost-sd__volume-bundle-variant-row');
    if (!variantRows.length) return { totalOriginal: 0, discounted: 0, selectedVariants: [] };

    let totalOriginal = 0;
    const selectedVariants: string[] = [];

    variantRows.forEach((row) => {
      const selected = row.querySelector('.boost-sd__dropdown-option--selected');
      const id =
        selected?.getAttribute('data-variant-id') || row.getAttribute('data-base-variant-id');
      const variant = product.variants.find(
        (v) => this.isValidVariant(v) && Number(v.id) === Number(id)
      );
      if (!variant) return;
      selectedVariants.push(String(id));
      totalOriginal += Number(variant.price);
    });

    const { rawDiscountedPrice: discounted } = this.calculateDiscountedPrice(
      totalOriginal,
      discount,
      Number(window.Shopify?.currency?.rate || 1)
    );

    return { totalOriginal, discounted, selectedVariants };
  }

  private handleVariantRow(
    row: Element,
    data: VolumeBundleCartItem[],
    selectedOption: VolumeBundleOption,
    volumeBundleData: VolumeBundleData,
    bundleProductId: number | undefined
  ): void {
    if (!row) return;

    const selectedItem = this.getSelectedItem(row);
    if (!selectedItem) return;

    const itemData = this.extractVariantItemData(selectedItem, volumeBundleData, selectedOption);
    if (!itemData) return;

    const { variantId, variantPrice, variantTitle } = itemData;
    const rowProductId =
      row.closest('.boost-sd__volume-bundle-menu')?.getAttribute('data-product-id') ||
      bundleProductId ||
      volumeBundleData?.matchedProduct?.id;

    data.push({
      id: String(variantId),
      quantity: 1,
      properties: {
        BoostBundle: '',
        _boostBundleId: volumeBundleData.widgetId,
        _boostBundleType: volumeBundleData.bundle_type,
        _boostItemPrice: variantPrice,
        _boostItemTitle: variantTitle,
        _boostBundleProducts: rowProductId,
      },
    });
  }

  private extractVariantItemData(
    selectedItem: Element,
    volumeBundleData: VolumeBundleData,
    selectedOption: VolumeBundleOption
  ): { variantId: number; variantPrice: number; variantTitle: string } | null {
    const rawId = selectedItem.getAttribute('data-variant-id');
    const variantPrice = selectedItem.getAttribute('data-variant-price');
    const variantTitle = selectedItem.getAttribute('data-variant-title');

    if (!rawId) return null;

    const product = volumeBundleData?.matchedProduct;
    const variant = product?.variants?.find(
      (v) => this.isValidVariant(v) && Number(v.id) === Number(rawId)
    );
    if (!variant || variant.available === false) {
      console.warn('Variant unavailable — skipping row.', selectedItem);
      return null;
    }

    return {
      variantId: Number(rawId),
      variantPrice: variantPrice
        ? Number(variantPrice)
        : selectedOption.selectedDropdownOptions.rawDiscountedPrice,
      variantTitle: variantTitle || selectedItem.textContent?.trim() || '',
    };
  }
}
